// AssistantPanel — the desktop BYOK API-key section, pure.
//
// BYOK, honestly stated: the author brings their own provider API key; the
// desktop app stores it in a local file on this computer and uses it directly
// against the provider. The copy says exactly that — no "your key is safe in
// the cloud" theatre, because there is no cloud.
//
// CREDENTIAL DISCIPLINE (mirrors app/desktopBridge.ts): the panel handles the
// key value ONLY on the way in. It shows presence ("configured" / "not
// configured"), never the stored value — there is no prop that could carry it,
// so this surface cannot leak it. The masked input holds the caller-owned
// DRAFT of a new key (apply-on-submit, not on-change: writing a credential is
// a deliberate act, unlike a theme toggle), and clearing is its own reported
// intent (never "save empty string").
//
// When there is no DesktopBridge (`keyState === "web_build"`) the section
// renders an honest note instead of dead controls — a disabled input that can
// never work is a false affordance, not a feature.
//
// Composition: SettingsSection + SettingsRow + Badge (presence) + Input
// (masked draft) + Button. Register: near-monochrome; "configured" is a
// genuine status, so it uses the success Badge tone with its required
// redundant icon (SC 1.4.1); clearing is destructive, so `danger`.
//
// Pure/props-driven: presence and draft arrive as props; every action reports
// UP (the consumer talks to the bridge and owns the clear confirmation).

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Badge } from "../../Badge/Badge.js";
import { Button } from "../../Button/Button.js";
import { Icon } from "../../Icon/Icon.js";
import { Input } from "../../Input/Input.js";
import { Switch } from "../../Switch/Switch.js";
import { SettingsRow } from "../SettingsRow/SettingsRow.js";
import { SettingsSection } from "../SettingsSection/SettingsSection.js";
import type { AssistantKeyState } from "../types.js";
import "./AssistantPanel.css";

export type AssistantPanelProps = {
    readonly keyState: AssistantKeyState;
    // The caller-held draft of a NEW key (never the stored one).
    readonly keyDraft: string;
    readonly onKeyDraftChange: (draft: string) => void;
    // Submit the draft; the consumer writes it through the bridge.
    readonly onKeySave: () => void;
    // Ask to clear the stored key; the consumer confirms before acting.
    readonly onKeyClearRequest: () => void;
    // Semantic-summary consent (IDD 94442b9d) — default off, the opt-in
    // trust surface: the assistant only summarizes activity once the
    // Author explicitly turns this on, and can turn it off again any time.
    readonly summaryConsent: boolean;
    readonly onSummaryConsentChange: (consented: boolean) => void;
    // The journal's own-text settings (reading consent + remove all), right
    // under the summary consent: every "what may the assistant do with what
    // I write" choice in one place. Rendered by the journal module.
    readonly journalTexts?: ReactNode;
};

const KEY_FIELD_ID = "assistant-api-key";

// The success Badge demands a redundant icon (status never rides hue alone).
const checkGlyph = <Icon name="check" size="xs" />;

// The consent row, shared by both the web-build note and the desktop key
// section below (semantic-summary consent is unrelated to BYOK — it always
// applies). A real Switch (controlled, no local state): the value arrives as
// `summaryConsent`, every change reports straight UP to the command dispatch.
const SUMMARY_CONSENT_SWITCH_ID = "assistant-summary-consent";
const SummaryConsentRow = ({
    summaryConsent,
    onSummaryConsentChange,
}: {
    readonly summaryConsent: boolean;
    readonly onSummaryConsentChange: (consented: boolean) => void;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <SettingsRow
            label={t`Summarize my activity`}
            description={t`The assistant writes short, timestamped summaries of what's happening in your workspace, so it can pick up where you left off. Off by default — turn it on or off any time.`}
            htmlFor={SUMMARY_CONSENT_SWITCH_ID}
            control={
                <Switch
                    checked={summaryConsent}
                    onCheckedChange={onSummaryConsentChange}
                    label={t`Summarize my activity`}
                />
            }
        />
    );
};

export const AssistantPanel = ({
    keyState,
    keyDraft,
    onKeyDraftChange,
    onKeySave,
    onKeyClearRequest,
    summaryConsent,
    onSummaryConsentChange,
    journalTexts,
}: AssistantPanelProps) => {
    const { t } = useLingui();

    if (keyState === "web_build")
        return (
            <SettingsSection
                title={t`Assistant`}
                description={t`What the assistant may do with your workspace.`}
            >
                <SummaryConsentRow
                    summaryConsent={summaryConsent}
                    onSummaryConsentChange={onSummaryConsentChange}
                />
                {journalTexts}
            </SettingsSection>
        );

    // keyState is now one of the three desktop positions; the Records below
    // are total over exactly those, so a new position fails to compile here.
    const statusBadge: Record<
        Exclude<AssistantKeyState, "web_build">,
        ReactNode
    > = {
        checking: <Badge tone="neutral">{t`Checking…`}</Badge>,
        configured: (
            <Badge tone="success" icon={checkGlyph}>
                {t`Configured`}
            </Badge>
        ),
        not_configured: <Badge tone="neutral">{t`Not configured`}</Badge>,
    };

    return (
        <SettingsSection
            title={t`Assistant`}
            description={t`You bring your own API key. It's stored in a local file on this computer and used directly against the provider — it never passes through anyone else's server.`}
        >
            <SettingsRow
                label={t`API key`}
                description={t`Whether a key is stored — the key itself is never shown.`}
                control={statusBadge[keyState]}
            />

            <SettingsRow
                label={keyState === "configured" ? t`Replace key` : t`Set key`}
                description={t`Paste a key from your provider account.`}
                htmlFor={KEY_FIELD_ID}
                control={
                    <form
                        className="bilda-assistant-panel__key-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            onKeySave();
                        }}
                    >
                        <Input
                            id={KEY_FIELD_ID}
                            type="password"
                            autoComplete="off"
                            spellCheck={false}
                            value={keyDraft}
                            onChange={(e) => onKeyDraftChange(e.target.value)}
                            aria-label={
                                keyState === "configured"
                                    ? t`Replace key`
                                    : t`Set key`
                            }
                        />
                        <Button
                            type="submit"
                            variant="secondary"
                            disabled={keyDraft.trim() === ""}
                        >
                            {t`Save key`}
                        </Button>
                    </form>
                }
            />

            {keyState === "configured" ? (
                <SettingsRow
                    label={t`Clear key`}
                    description={t`Remove the key from this computer. The assistant stops working until you set a new one.`}
                    control={
                        <Button variant="danger" onClick={onKeyClearRequest}>
                            {t`Clear key`}
                        </Button>
                    }
                />
            ) : null}

            <SummaryConsentRow
                summaryConsent={summaryConsent}
                onSummaryConsentChange={onSummaryConsentChange}
            />
            {journalTexts}
        </SettingsSection>
    );
};
